import {
  DOCUMENT_PUBLISH_METADATA_FILES,
  FOLDER_PUBLISH_METADATA_FILES,
} from "../../lib/publish-utils";
import { getPlatformService } from "../../platform";
import { useSettingsStore } from "../settings-store";
import type {
  EditorState,
  FileTreeNode,
  PaneLayout,
  SidebarPresentation,
  SidebarView,
  ViewMode,
} from "./types";

/**
 * Selector that returns the effective view mode for the currently focused
 * pane, or the global `viewMode` when no pane is materialized (single-pane
 * mode). Slice mutators keep these two values in sync (Phase 3 #20), so
 * `state.viewMode` alone is also a valid read; this selector exists as the
 * canonical accessor for callers that want intent-revealing code.
 */
export function selectEffectiveViewMode(state: EditorState): ViewMode {
  const focused = state.panes.find((p) => p.paneId === state.focusedPaneId);
  return focused?.viewMode ?? state.viewMode;
}

export function getPaneLayoutLeafIds(
  layout: PaneLayout | null,
  options: { includeCollapsed?: boolean } = {},
): string[] {
  if (!layout) return [];
  if (layout.type === "leaf") {
    if (!options.includeCollapsed && layout.collapsed) return [];
    return [layout.paneId];
  }
  if (!options.includeCollapsed && layout.collapsed) return [];
  return layout.children.flatMap((child) =>
    getPaneLayoutLeafIds(child, options),
  );
}

export function countExpandedPaneLeaves(layout: PaneLayout | null): number {
  return getPaneLayoutLeafIds(layout).length;
}

export function isPaneCollapsedInLayout(
  layout: PaneLayout | null,
  paneId: string,
): boolean {
  if (!layout) return false;
  if (layout.type === "leaf") {
    return layout.paneId === paneId ? layout.collapsed === true : false;
  }
  if (
    layout.collapsed &&
    getPaneLayoutLeafIds(layout, { includeCollapsed: true }).includes(paneId)
  ) {
    return true;
  }
  return layout.children.some((child) => isPaneCollapsedInLayout(child, paneId));
}

export function setPaneCollapsedInLayout(
  layout: PaneLayout,
  paneId: string,
  collapsed: boolean,
): PaneLayout {
  if (layout.type === "leaf") {
    if (layout.paneId !== paneId) return layout;
    if (collapsed) return { ...layout, collapsed: true };
    const { collapsed: _collapsed, ...expandedLeaf } = layout;
    return expandedLeaf;
  }
  return {
    ...layout,
    children: layout.children.map((child) =>
      setPaneCollapsedInLayout(child, paneId, collapsed),
    ),
  };
}

export function getPaneLayoutNodeAtPath(
  layout: PaneLayout | null,
  path: number[],
): PaneLayout | null {
  if (!layout) return null;
  let node = layout;
  for (const index of path) {
    if (node.type === "leaf") return null;
    const child = node.children[index];
    if (!child) return null;
    node = child;
  }
  return node;
}

export function setPaneLayoutNodeCollapsedInLayout(
  layout: PaneLayout,
  path: number[],
  collapsed: boolean,
): PaneLayout {
  if (path.length === 0) {
    if (layout.type === "leaf") {
      if (collapsed) return { ...layout, collapsed: true };
      const { collapsed: _collapsed, ...expandedLeaf } = layout;
      return expandedLeaf;
    }
    if (collapsed) return { ...layout, collapsed: true };
    const { collapsed: _collapsed, ...expandedBranch } = layout;
    return expandedBranch;
  }
  if (layout.type === "leaf") return layout;
  const [head, ...rest] = path;
  return {
    ...layout,
    children: layout.children.map((child, index) =>
      index === head
        ? setPaneLayoutNodeCollapsedInLayout(child, rest, collapsed)
        : child
    ),
  };
}

export function getSidebarPresentation(view: SidebarView): SidebarPresentation {
  return view === "settings" ? "content" : "docked";
}

export function splitPercentForViewMode(viewMode: ViewMode): number {
  return viewMode === "editor" ? 100 : viewMode === "preview" ? 0 : 50;
}

export function viewModeForSplitPercent(pct: number): ViewMode {
  return pct >= 100 ? "editor" : pct <= 0 ? "preview" : "split";
}

/* ── File tree helpers: bundle-aware child building ── */

export interface DirEntry {
  name: string;
  isDirectory: boolean;
  isSymlink?: boolean;
  targetPath?: string;
}

/** Check whether a FileTreeNode is expandable (directory or bundled .md) */
export function isExpandableNode(node: FileTreeNode): boolean {
  return node.isDirectory || !!node.bundlePath;
}

function findPairedBundleDirs(entries: DirEntry[]): Set<string> {
  const mdFileStems = new Set<string>();
  const bundleDirNames = new Set<string>();
  for (const e of entries) {
    if (!e.isDirectory && e.name.endsWith(".md")) {
      mdFileStems.add(e.name.slice(0, -3));
    }
    if (e.isDirectory && e.name.endsWith(".magic-md")) {
      bundleDirNames.add(e.name);
    }
  }

  const pairedBundleDirs = new Set<string>();
  for (const stem of mdFileStems) {
    const bundleName = `${stem}.magic-md`;
    if (bundleDirNames.has(bundleName)) {
      pairedBundleDirs.add(bundleName);
    }
  }
  return pairedBundleDirs;
}

/**
 * Build visible children from raw workspace entries.
 * Hides paired .magic-md dirs and attaches bundlePath to their .md siblings.
 */
export function buildVisibleChildren(
  parentPath: string,
  entries: DirEntry[],
): FileTreeNode[] {
  const pairedBundleDirs = findPairedBundleDirs(entries);

  const nodes: FileTreeNode[] = [];
  for (const e of entries) {
    if (pairedBundleDirs.has(e.name)) continue; // Hide paired bundle dirs
    const node: FileTreeNode = {
      name: e.name,
      path: `${parentPath}/${e.name}`,
      isDirectory: e.isDirectory,
      isSymlink: e.isSymlink,
      targetPath: e.targetPath,
      children: e.isDirectory ? [] : undefined,
      loaded: false,
      expanded: false,
    };
    // Attach bundlePath to .md files with a paired .magic-md directory
    if (!e.isDirectory && e.name.endsWith(".md")) {
      const stem = e.name.slice(0, -3);
      if (pairedBundleDirs.has(`${stem}.magic-md`)) {
        node.bundlePath = `${parentPath}/${stem}.magic-md`;
        node.children = []; // Make it expandable
      }
    }
    nodes.push(node);
  }

  nodes.sort((a, b) => {
    if (a.isDirectory !== b.isDirectory) return a.isDirectory ? -1 : 1;
    return a.name.localeCompare(b.name);
  });
  return nodes;
}

/** Build children for virtual directories — all children marked isVirtual */
export function buildVirtualChildren(
  parentPath: string,
  entries: DirEntry[],
): FileTreeNode[] {
  const pairedBundleDirs = findPairedBundleDirs(entries);
  return entries
    .filter((e) => !pairedBundleDirs.has(e.name))
    .map((e) => {
      const node: FileTreeNode = {
        name: e.name,
        path: `${parentPath}/${e.name}`,
        isDirectory: e.isDirectory,
        isSymlink: e.isSymlink,
        targetPath: e.targetPath,
        isVirtual: true as const,
        children: e.isDirectory ? ([] as FileTreeNode[]) : undefined,
        loaded: false,
        expanded: false,
      };
      if (!e.isDirectory && e.name.endsWith(".md")) {
        const stem = e.name.slice(0, -3);
        if (pairedBundleDirs.has(`${stem}.magic-md`)) {
          node.bundlePath = `${parentPath}/${stem}.magic-md`;
          node.children = [];
        }
      }
      return node;
    })
    .sort((a, b) => {
      if (a.isDirectory !== b.isDirectory) return a.isDirectory ? -1 : 1;
      return a.name.localeCompare(b.name);
    });
}

export function buildBundleVirtualChildren(
  bundlePath: string,
  entries: DirEntry[],
): FileTreeNode[] {
  const directories = buildVirtualChildren(
    bundlePath,
    entries.filter((entry) => entry.isDirectory),
  );
  return directories;
}

export function buildPublishVirtualChildren(
  bundlePath: string,
  publishEntries: DirEntry[],
): FileTreeNode[] {
  const metadataNodes = DOCUMENT_PUBLISH_METADATA_FILES.filter((entry) =>
    publishEntries.some(
      (publishEntry) =>
        !publishEntry.isDirectory && publishEntry.name === entry.name,
    ),
  ).map((entry) => ({
    name: entry.name,
    path: entry.getPath(bundlePath),
    isDirectory: false,
    isVirtual: true as const,
    loaded: false,
    expanded: false,
  }));

  const artifactNodes = buildVirtualChildren(
    `${bundlePath}/publish`,
    publishEntries,
  ).filter(
    (entry) =>
      !metadataNodes.some((metadataNode) => metadataNode.name === entry.name),
  );

  return [...metadataNodes, ...artifactNodes];
}

/**
 * Build virtual children for a folder-level .magic-md/publish directory.
 * Shows metadata files (config, status, report) and the dist/ directory.
 */
export function buildFolderPublishVirtualChildren(
  publishDirPath: string,
  entries: DirEntry[],
): FileTreeNode[] {
  const metadataNodes = FOLDER_PUBLISH_METADATA_FILES.filter((entry) =>
    entries.some(
      (e) => !e.isDirectory && e.name === entry.name,
    ),
  ).map((entry) => ({
    name: entry.name,
    path: entry.getPath(publishDirPath),
    isDirectory: false,
    isVirtual: true as const,
    loaded: false,
    expanded: false,
  }));

  const artifactNodes = buildVirtualChildren(
    publishDirPath,
    entries,
  ).filter(
    (entry) =>
      !metadataNodes.some((mn) => mn.name === entry.name),
  );

  return [...metadataNodes, ...artifactNodes];
}

/** Remove a pane leaf from the layout tree, collapsing single-child parents */
export function removePaneFromLayout(
  layout: PaneLayout,
  paneId: string,
): PaneLayout | null {
  if (layout.type === "leaf") {
    return layout.paneId === paneId ? null : layout;
  }
  const newChildren: PaneLayout[] = [];
  const newSizes: number[] = [];
  for (let i = 0; i < layout.children.length; i++) {
    const result = removePaneFromLayout(layout.children[i], paneId);
    if (result) {
      newChildren.push(result);
      newSizes.push(layout.sizes[i] ?? 50);
    }
  }
  if (newChildren.length === 0) return null;
  if (newChildren.length === 1) return newChildren[0]; // Collapse single child
  const total = newSizes.reduce((a, b) => a + b, 0);
  const normalized = newSizes.map((s) => (s / total) * 100);
  return { ...layout, children: newChildren, sizes: normalized } as PaneLayout;
}

export async function reloadSettingsContext(): Promise<void> {
  try {
    const settings = useSettingsStore.getState();
    await Promise.all([settings.loadSchema(), settings.loadSnapshot()]);
  } catch {
    // Ignore when settings transport is not ready yet.
  }
}

export async function persistEditorUserSetting(
  dotPath: string,
  value: unknown,
): Promise<void> {
  try {
    await getPlatformService().settings.setValue({
      owner: "editor",
      target: "user",
      dotPath,
      value,
    });
    await useSettingsStore.getState().loadSnapshot();
  } catch {
    // Ignore when the platform service is not yet initialized or persistence fails.
  }
}

let tabCounter = 1;
export function newTabId(): string {
  return `tab-${tabCounter++}`;
}
